import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import type { OrganizerStatus } from "@ticketnjoy/types";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { QueryError } from "@/components/query-error";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ORGANIZER_PAGE_SIZE, useOrganizersQuery } from "@/features/api";
import {
  ORGANIZER_STATUS_LABELS,
  dateOnly,
  organizerStatusVariant,
  percent,
} from "@/lib/format";

const STATUS_FILTERS: OrganizerStatus[] = [
  "Active",
  "PendingEmailVerification",
  "Suspended",
  "Deleted",
];
const ALL = "all";

export function OrganizersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const rawStatus = searchParams.get("status") as OrganizerStatus | null;
  const status = rawStatus && STATUS_FILTERS.includes(rawStatus) ? rawStatus : "";
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);

  const [searchInput, setSearchInput] = useState(q);

  function update(next: { q?: string; status?: string; page?: number }) {
    const params = new URLSearchParams(searchParams);
    const merged = { q, status, page: 1, ...next };
    for (const [key, value] of Object.entries(merged)) {
      if (!value || (key === "page" && value === 1)) params.delete(key);
      else params.set(key, String(value));
    }
    setSearchParams(params, { replace: true });
  }

  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === q) return;
    const t = setTimeout(() => update({ q: trimmed }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sadece input değişince
  }, [searchInput]);

  const { data, isLoading, isFetching, error, refetch } = useOrganizersQuery({ q, status, page });
  const pageCount = data ? Math.max(1, Math.ceil(data.total / ORGANIZER_PAGE_SIZE)) : 1;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Organizatörler</h1>
        <p className="text-sm text-muted-foreground">
          {data ? `${data.total.toLocaleString("tr-TR")} kayıt` : "Yükleniyor..."}
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Firma adı, ünvan veya e-posta"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <Select
          value={status || ALL}
          onValueChange={(v) => update({ status: v === ALL ? "" : v })}
        >
          <SelectTrigger className="sm:w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tüm durumlar</SelectItem>
            {STATUS_FILTERS.map((s) => (
              <SelectItem key={s} value={s}>
                {ORGANIZER_STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="py-0">
        <CardContent className="px-0">
          {error ? (
            <QueryError
              className="m-6"
              message="Liste yüklenemedi."
              onRetry={() => refetch()}
              retrying={isFetching}
            />
          ) : isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Yükleniyor...</p>
          ) : data && data.items.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">Kayıt bulunamadı.</p>
          ) : (
            <div className={isFetching ? "overflow-x-auto opacity-60" : "overflow-x-auto"}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Organizatör</th>
                    <th className="px-4 py-3 font-medium">Durum</th>
                    <th className="px-4 py-3 text-right font-medium">Komisyon</th>
                    <th className="px-4 py-3 text-right font-medium">Servis ücreti</th>
                    <th className="px-4 py-3 text-right font-medium">Mağaza</th>
                    <th className="px-4 py-3 font-medium">Son satış</th>
                    <th className="px-4 py-3 font-medium">Kayıt</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.items.map((o) => (
                    <tr key={o.id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="px-4 py-3">
                        <Link to={`/organizers/${o.id}`} className="font-medium hover:underline">
                          {o.companyName}
                        </Link>
                        <div className="text-xs text-muted-foreground">{o.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={organizerStatusVariant(o.status)}>
                          {ORGANIZER_STATUS_LABELS[o.status] ?? o.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {percent(o.commissionRate)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {o.serviceFee.toLocaleString("tr-TR", { maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{o.shopCount}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                        {dateOnly(o.lastSaleAt)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                        {dateOnly(o.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {pageCount > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => update({ page: page - 1 })}
          >
            Önceki
          </Button>
          <span className="text-muted-foreground">
            {page} / {pageCount}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= pageCount}
            onClick={() => update({ page: page + 1 })}
          >
            Sonraki
          </Button>
        </div>
      )}
    </div>
  );
}
